<template>
  <div class="packages-form">
    <tabs v-if="permissions.length > 0">
      <tab
        class="mt-3"
        name="Details"
      >
        <form-control
          class="mb-3"
          v-model="data.name"
          :errors="error ? error.errors.name : []"
          :no-margin="true"
          title="Package Name"
        />

        <form-control
          class="mb-3"
          v-model="data.description"
          :errors="error ? error.errors.description : []"
          title="Package Description"
          :no-margin="true"
        />

        <label class="mr-2">
          All Articles
        </label>
        <toggle-button
          v-model="allArticles"
          :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
        />
        <div v-if="!allArticles">
          <div class="form-group py-2 border-top">
            <label>Products</label>
            <quick-selector
              :options="entitlementPermissions.filter(({ entitlement_type }) => (entitlement_type || '').includes('Product'))"
              :selected="data.permissions"
              v-model="data.permissions"
              class="quick-selection--authors my-1"
              placeholder="Start typing to add new products..."
            >
              <template
                slot="option"
                slot-scope="scope"
                v-if="scope.option.entitlement"
              >
              {{ scope.option.entitlement.name }}
              </template>
            </quick-selector>

            <div class="tag-input">
              <div class="tag-input__list">
                <div
                  v-for="permission in productPermissions"
                  :key="permission.id"
                  class="tag-input__list__item"
                >
                  <span>{{ permission.entitlement.name }}</span>
                  <span @click="removePermission(permission.id)">
                    <icon
                      icon="remove-tag"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group m-0 py-2 border-top">
            <label>Regions</label>
            <quick-selector
              :options="entitlementPermissions.filter(({ entitlement_type }) => (entitlement_type || '').includes('Region'))"
              :selected="data.permissions"
              v-model="data.permissions"
              class="quick-selection--authors my-1"
              placeholder="Start typing to add new regions..."
            >
              <template
                slot="option"
                slot-scope="scope"
                v-if="scope.option.entitlement"
              >
                <span>{{ scope.option.entitlement.name }}</span>
              </template>
            </quick-selector>

            <div class="tag-input">
              <div class="tag-input__list">
                <div
                  v-for="permission in regionPermissions"
                  :key="permission.id"
                  class="tag-input__list__item"
                >
                  <span>{{ permission.entitlement.name }}</span>
                  <span @click="removePermission(permission.id)">
                    <icon
                      icon="remove-tag"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="label">
              Providers
            </div>
            <div
              class="btn-group"
              role="group"
              aria-label="Basic example"
            >
              <button
                type="button"
                class="btn btn-primary"
                :class="{ active: allChannels }"
                @click="allChannels = true"
              >
                All providers
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :class="{ active: selectProviders }"
                @click="selectProviders = true"
              >
                Select providers
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :class="{ active: associatedProviders }"
                @click="associatedProviders = true"
              >
                User's associated providers
              </button>
            </div>
          </div>

          <div
            v-if="associatedProviders === false && allChannels === false"
            class="form-group m-0 py-2 border-top"
          >
            <label>Channels</label>
            <quick-selector
              :options="entitlementPermissions.filter(({ entitlement_type, entitlement }) => (entitlement_type || '').includes('Channel') && !!entitlement)"
              :selected="data.permissions"
              :disabled="allChannels"
              v-model="data.permissions"
              class="quick-selection--authors my-1"
              placeholder="Start typing to add new channels..."
            >
              <template
                slot="option"
                slot-scope="scope"
                v-if="scope.option.entitlement"
              >
                <span>{{ scope.option.entitlement.name }}</span>
              </template>
            </quick-selector>

            <div class="tag-input">
              <div class="tag-input__list">
                <div
                  v-for="permission in channelPermissions"
                  :key="permission.id"
                  class="tag-input__list__item"
                >
                  <span>{{ permission.entitlement.name }}</span>
                  <span @click="removePermission(permission.id)">
                    <icon
                      icon="remove-tag"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </tab>
      <tab
        v-if="$login.can('roles.advanced-permissions')"
        name="Permissions"
      >
        <div
          class="d-flex flex-wrap align-items-stretch"
          style="margin: 0 -1rem;"
        >
          <div
            v-for="(group, name) in groupedPermissions"
            :key="name"
            style="width: 25%"
            class="my-3"
          >
            <div class="bg-gray border rounded p-4 h-100 mx-3">
              <div class="text-center mb-2">
                {{ capitalize(name) }}
              </div>
              <div
                v-for="(permission, index) in group"
                :key="index"
                class="mb-2"
              >
                <toggle-button
                  v-tooltip="permission.description"
                  v-model="permission.on"
                  :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
                />
                {{ prettify(permission.name) }}
              </div>
            </div>
          </div>
        </div>
      </tab>
    </tabs>
    <confirm
      v-if="data && data.name"
      @confirmed="deletePackage"
      :name="data.name"
    />


    <div class="border-top py-3 d-flex align-items-center save-footer">
      <button
        v-if="!$route.name.includes('create') && $login.can('roles.delete')"
        class="mr-auto btn  btn-outline-danger"
        @click="confirmDelete"
      >
        Delete Package
      </button>

      <button
        class="btn btn-primary "
        @click="save"
      >
        Save
      </button>
    </div>
  </div>
</template>

<script>
import QuickSelector from '@/components/QuickSelector'
import {capitalize, getOnce} from '@/helpers'
import Tab from '@/components/Tab'
import Tabs from '@/components/Tabs'
import {successNotification} from '@/notifications'
import Confirm from '@/components/Confirm'
import { ToggleButton } from 'vue-js-toggle-button'

const defaultData = {
  name: null,
  permissions: [],
}

export default {
  components: {
    QuickSelector,
    Confirm,
    Tab,
    Tabs,
    ToggleButton,
  },

  props: {
    value: {
      required: false,
      type: Object,
      default () {
        return Object.assign({}, defaultData)
      },
    },

  },

  data () {
    return {
      data: {},
      error: null,
      loading: false,
      permissions: [],
      allChannelsIndex: null,
      allArticlesIndex: null,
      associatedChannelsIndex: null,
      firstTime: true,
      channels: [],
    }
  },

  computed: {
    entitlementPermissions () {
      return this.permissions.filter(p => !!p.entitlement)
    },

    groupedPermissions () {
      const grouped = {}
      for (const permission of this.permissions) {
        const split = permission.name.split('.')
        const name = split[0]
        if (name !== 'view') {
          if (!grouped[name]) {
            grouped[name] = []
          }
          grouped[name].push(permission)
        }
      }
      return grouped
    },

    allChannels: {
      get () {
        if (!this.data || !this.allChannelsIndex) {
          return false
        }

        return this.permissions[this.allChannelsIndex].on
      },

      set (value) {
        if (value) {
          this.selectProviders = false
          this.associatedProviders = false
        }
        this.permissions[this.allChannelsIndex].on = value
      },
    },

    selectProviders: {
      get () {
        if (!this.data || (!this.allChannelsIndex && !this.associatedChannelsIndex)) {
          return false
        }

        return this.permissions[this.allChannelsIndex].on === false && this.permissions[this.associatedChannelsIndex].on === false
      },

      set (value) {
        if (value) {
          this.associatedProviders = false
          this.allChannels = false
        }
      },
    },


    associatedProviders: {
      get () {
        if (!this.data || !this.associatedChannelsIndex) {
          return false
        }

        return this.permissions[this.associatedChannelsIndex].on
      },

      set (value) {
        if (value) {
          this.selectProviders = false
          this.allChannels = false
        }
        this.permissions[this.associatedChannelsIndex].on = value
      },
    },

    allArticles: {
      get () {
        if (!this.data || !this.allArticlesIndex) {
          return false
        }

        return this.permissions[this.allArticlesIndex].on
      },

      set (value) {
        this.permissions[this.allArticlesIndex].on = value
      },
    },

    productPermissions() {
      console.log(this.data.permissions.filter((permission) => permission.entitlement_type === 'App\\Models\\Product'))
      return this.data.permissions.filter((permission) => permission.entitlement_type === 'App\\Models\\Product')
    },

    regionPermissions() {
      return this.data.permissions.filter((permission) => permission.entitlement_type === 'App\\Models\\Region')
    },

    channelPermissions() {
      return this.data.permissions.filter((permission) => permission.entitlement_type === 'App\\Models\\Channel' && !!permission.entitlement)
    },

  },

  watch: {
    value: {
      deep: true,
      handler () {
        this.getPermissions()
        this.data = this.value
      },
    },

    data: {
      deep: true,
      handler () {
        this.$emit('input', this.data)
        this.$nextTick(() => {
          for (const permission of this.data.permissions) {
            const index = this.permissionIndexOf(permission.id)
            if (index !== -1 && !this.permissions[index].on) {
              this.permissions[index].on = true
            }
          }
        })
      },
    },

    permissions: {
      deep: true,
      handler () {
        for (const permission of this.permissions) {
          const index = this.indexOf(permission.id)
          if (!permission.on && index !== -1) {
            this.data.permissions.splice(index, 1)
          } else if (permission.on && index === -1) {
            this.data.permissions.push(permission)
          }
        }
      },
    },

  },

  mounted () {
    this.data = this.value
    this.getPermissions()
    this.getChannels()
  },

  methods: {
    confirmDelete () {
      this.$modal.show('confirm-delete')
    },

    removePermission (permissionId) {
      const index = this.data.permissions.findIndex(p => p.id === permissionId)
      if (index !== -1) {
        this.data.permissions.splice(index, 1)
      }
    },

    async deletePackage () {
      await this.$http.delete('/roles/' + this.data.id)
      this.$notify(successNotification(`Deleted ${this.data.name}`))
      this.$router.go(-1)
    },

    indexOf (permissionId) {
      for (const index in this.data.permissions) {
        if (this.data.permissions[index] && this.data.permissions[index].id === permissionId) {
          return parseInt(index, 10)
        }
      }

      return -1
    },
    permissionIndexOf (permissionId) {
      for (const index in this.permissions) {
        if (this.permissions[index] && this.permissions[index].id === permissionId) {
          return parseInt(index, 10)
        }
      }

      return -1
    },
    getPermissions () {
      return getOnce('permissions').then((r) => {
        const allChannels = r.data.data.find((permission) => permission.name === 'view.articles.channel.*')
        const associatedChannel = r.data.data.find((permission) => permission.name === 'articles.view.channel')
        const allArticles = r.data.data.find((permission) => permission.name === 'articles.view')
        this.permissions = r.data.data.map((v) => {
          v.on = this.data.permissions.filter((permission) => permission.id === v.id).length > 0
          if (!this.data.id && allChannels.id === v.id && this.firstTime) {
            v.on = true
            this.firstTime = false
          }
          return v
        })
        this.allArticlesIndex = this.permissionIndexOf(allArticles.id)
        this.associatedChannelsIndex = this.permissionIndexOf(associatedChannel.id)
        this.allChannelsIndex = this.permissionIndexOf(allChannels.id)
      })
    },

    getChannels () {
      getOnce('channels').then((r) => this.channels = r.data.data)
    },

    prettify (permission) {
      const split = permission.split('.')
      split.shift()
      const val = split.join('-')

      return capitalize(val.replace(/-/g, ' '))
    },

    capitalize (v) {
      return capitalize(v)
    },

    getPermissionsByType (type) {
      return this.$http.get(`/permissions?entitlement_type=${type}`)
    },

    execute () {
      return this.data.id
        ? this.$http.put(`/roles/${this.data.id}`, this.data)
        : this.$http.post('/roles', this.data)
    },
    save () {
      if (!this.loading) {
        this.loading = true
        this.execute()
            .then((response) => {
              this.loading = false
              this.$emit('save-success', response)
            })
            .catch((error) => {
              this.loading = false
              if (
                error.response &&
                error.response.data &&
                error.response.data.errors
              ) {
                this.error = error.response.data
              }
              this.$emit('save-fail', error)
            })
      }
    },
  },
}
</script>
